import { Link, useLocation } from "react-router";
import { useAuth } from "@/contexts/auth-context";
import { useState, useEffect } from "react";
import { useTranslations } from '@/i18n/use-translations';
import {
  LayoutDashboard,
  Activity,
  Database,
  GitBranch,
  MessageSquare,
  Table2,
  Globe,
  Users,
  Code2,
  Wrench,
  ScrollText,
  Webhook,
  ChevronLeft,
  Wifi,
  Package,
  FileText,
  Shield,
  Building2,
  HeartPulse,
  BookOpen,
  Layers,
  ShieldCheck,
  BarChart3,
  KeyRound,
  Mail,
  UserCog,
  ListChecks,
  MailPlus,
  Radio,
  BrainCircuit,
  Bot,
  Puzzle,
  CreditCard,
  Settings,
  Inbox,
} from "lucide-react";
import { useOrg } from "@/hooks/use-org";
import { useMessageBadge } from "@/hooks/use-message-badge";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { shouldShowNav, isNavActive } from "@/lib/formatters";
import { useCapabilities } from "@/hooks/use-capabilities";


interface NavGroup {
  labelKey: string;
  items: NavItem[];
}

interface NavItem {
  href: string;
  labelKey?: string;
  label?: string;
  icon: React.ElementType;
  soon?: boolean;
  beta?: boolean;
  preview?: boolean;
  superAdminOnly?: boolean;
  /** Hide unless the user has a specific capability from their RBAC roles */
  requiresCapability?: 'canManageRoles' | 'canManageUsers' | 'canManageSettings';
  hidden?: boolean;
  /** Custom badge text (e.g. "PRO") shown next to label */
  badge?: string;
  /** Show live pending-messages count badge */
  pendingCount?: boolean;
}

const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: "overview",
    items: [
      { href: "/dashboard", labelKey: "dashboard", icon: LayoutDashboard },
      { href: "/activity", labelKey: "activity", icon: Activity },
    ],
  },
  {
    labelKey: "build",
    items: [
      { href: "/settings/entities", labelKey: "entities", icon: Database },
      { href: "/settings/rules", labelKey: "businessRules", icon: GitBranch },
      { href: "/agents", labelKey: "agents", icon: Bot },
      { href: "/integrations", labelKey: "integrationsNav", icon: Puzzle },
      { href: "/channels", labelKey: "channels", icon: MessageSquare, soon: true, superAdminOnly: true },
      { href: "/apps", labelKey: "apps", icon: Package, soon: true, superAdminOnly: true },
      { href: "/settings/templates", labelKey: "pdfTemplates", icon: FileText, superAdminOnly: true },
    ],
  },
  {
    labelKey: "manage",
    items: [
      { href: "/entities", labelKey: "dataBrowser", icon: Table2 },
      { href: "/views", labelKey: "views", icon: Layers },
      { href: "/sites", labelKey: "sites", icon: Globe },
      { href: "/users", labelKey: "users", icon: Users },
      { href: "/invitations", labelKey: "invitations", icon: MailPlus, preview: true, superAdminOnly: true, hidden: true },
      { href: "/platform/invitations", labelKey: "invitations", icon: MailPlus, preview: true, hidden: true },
      { href: "/roles", labelKey: "roles", icon: ShieldCheck, requiresCapability: 'canManageRoles' },
      { href: "/knowledge", labelKey: "knowledge", icon: BookOpen, preview: true },
      { href: "/agents/communications", labelKey: "agentCommunications", icon: GitBranch },
      { href: "/tasks", labelKey: "tasks", icon: ListChecks },
      { href: "/messages", labelKey: "messages", icon: Inbox, pendingCount: true },
    ],
  },
  {
    labelKey: "develop",
    items: [
      { href: "/api-explorer", labelKey: "apiExplorer", icon: Code2 },
      { href: "/settings/ai-provider", labelKey: "aiProvider", icon: BrainCircuit },
      { href: "/settings/ai-cost", labelKey: "aiCost", icon: BarChart3 },
      { href: "/settings/ai-templates", labelKey: "aiTemplates", icon: FileText },
      { href: "/settings/integrations", labelKey: "integrationsCatalog", icon: Package },
      { href: "/settings/mcp", labelKey: "integrations", icon: Wrench, soon: true, superAdminOnly: true },
      { href: "/settings/mcp-profiles", labelKey: "integrationProfiles", icon: UserCog, soon: true, superAdminOnly: true },
      { href: "/settings/api-keys", labelKey: "apiKeys", icon: KeyRound },
      { href: "/settings/webhooks", labelKey: "webhooks", icon: Webhook, soon: true, superAdminOnly: true },
      { href: "/settings/realtime", labelKey: "realtimeConnections", icon: Radio, superAdminOnly: true },
      { href: "/settings/email-logs", labelKey: "emailLogs", icon: Mail },
      { href: "/settings/rules/execution-logs", labelKey: "ruleLogs", icon: ListChecks },
      { href: "/logs", labelKey: "logs", icon: ScrollText },
      { href: "/usage", labelKey: "usage", icon: BarChart3 },
    ],
  },
];

const PLATFORM_NAV_GROUP: NavGroup = {
  labelKey: "platform",
  items: [
    { href: "/superadmin/overview", label: 'Platform Console', icon: LayoutDashboard },
    { href: "/superadmin/tenants", labelKey: "platformTenants", icon: Building2 },
    { href: "/superadmin/users", labelKey: "platformUsers", icon: Shield },
    { href: "/superadmin/health", labelKey: "platformHealth", icon: HeartPulse },
  ],
};

export function SideNav() {
  const { pathname } = useLocation();
  const { isAuthenticated, isSuperAdmin, isTenantUser, tenant } = useAuth();
  const { currentOrg, isOwner } = useOrg();
  const { capabilities } = useCapabilities();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const t = useTranslations("nav");
  const { pendingCount } = useMessageBadge(tenant?.slug ?? null);

  // Organization nav group — only for admin users with an org
  const orgNavGroup: NavGroup | null = (!isTenantUser && currentOrg) ? {
    labelKey: 'organization',
    items: [
      {
        href: '/org/members',
        labelKey: 'orgMembers',
        icon: Users,
        badge: currentOrg.plan === 'free' ? 'PRO' : undefined,
      },
      {
        href: '/org/billing',
        labelKey: 'orgBilling',
        icon: CreditCard,
      },
      ...(isOwner ? [{
        href: '/org/settings',
        labelKey: 'orgSettings',
        icon: Settings,
      }] : []),
    ],
  } : null;

  const navGroups = [
    ...(orgNavGroup ? [orgNavGroup] : []),
    ...NAV_GROUPS,
    ...(isSuperAdmin ? [PLATFORM_NAV_GROUP] : []),
  ];

  const visibleItems = (items: NavItem[]) =>
    (isSuperAdmin ? items : items.filter((i) => !i.superAdminOnly))
      .filter((i) => !i.hidden)
      .filter((i) => {
        if (!i.requiresCapability) return true;
        // SuperAdmins already passed the superAdminOnly filter above
        return isSuperAdmin || capabilities[i.requiresCapability];
      });

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  if (!shouldShowNav(pathname, isAuthenticated) || pathname === "/tenants") {
    return null;
  }

  const isActive = (href: string) => isNavActive(href, pathname);

  const sidebarContent = (
    <>
      <div className="flex-1 overflow-y-auto py-2">
        {navGroups.map((group, gi) => (
          <div key={group.labelKey}>
            {gi > 0 && <Separator className="my-3 opacity-50" />}
            {!collapsed && (
              <div data-testid={`nav-group-${group.labelKey}`} className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t(group.labelKey)}
              </div>
            )}
            <nav className="flex flex-col gap-0.5 px-2">
              {visibleItems(group.items).map((item) => {
                const active = isActive(item.href);
                const Icon = item.icon;
                const label = item.label ?? (item.labelKey ? t(item.labelKey) : item.href);
                const linkContent = (
                  <Link
                    key={item.href}
                    to={item.href}
                    data-testid={`nav-link-${item.href.replace(/\//g, '-').replace(/^-/, '')}`}
                    className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-150 ${
                      active
                        ? "border-l-2 border-violet bg-transparent text-foreground font-medium pl-[calc(0.75rem-2px)]"
                        : "text-sidebar-foreground/70 hover:bg-muted/50 hover:text-sidebar-foreground border-l-2 border-transparent"
                    } ${collapsed ? "justify-center px-2 border-l-0" : ""}`}
                  >
                    <Icon className={`h-4 w-4 shrink-0 ${active ? "text-violet" : ""}`} />
                    {!collapsed && (
                      <>
                        <span className="flex-1">{label}</span>
                        {item.pendingCount && pendingCount > 0 && (
                          <span className="rounded-full px-1.5 py-0.5 text-[9px] font-semibold bg-violet text-white min-w-[18px] text-center">
                            {pendingCount > 99 ? '99+' : pendingCount}
                          </span>
                        )}
                        {item.badge && (
                          <span className="rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-violet/10 text-violet">
                            {item.badge}
                          </span>
                        )}
                        {item.beta && !item.badge && (
                          <Badge variant="beta">Beta</Badge>
                        )}
                        {item.preview && !item.badge && (
                          <Badge variant="preview">Preview</Badge>
                        )}
                        {item.soon && !item.beta && !item.preview && !item.badge && (
                          <span className="rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-muted text-muted-foreground">
                            Soon
                          </span>
                        )}
                      </>
                    )}
                  </Link>
                );

                if (collapsed) {
                  return (
                    <Tooltip key={item.href} delayDuration={0}>
                      <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                      <TooltipContent side="right" sideOffset={8}>
                        {label}
                      </TooltipContent>
                    </Tooltip>
                  );
                }

                return linkContent;
              })}
            </nav>
          </div>
        ))}
      </div>

      {/* Footer: MCP status + collapse toggle */}
      <div className="shrink-0 border-t border-sidebar-border px-3 py-3">
        <div className={`mb-2 flex items-center gap-2 text-xs text-muted-foreground ${collapsed ? "justify-center" : "px-1"}`}>
          <Wifi className="h-3 w-3 shrink-0 text-success" />
          {!collapsed && <span>{t("mcpConnected")}</span>}
        </div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex w-full items-center justify-center rounded-md p-1.5 text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
          aria-label={collapsed ? t("expandSidebar") : t("collapseSidebar")}
        >
          <ChevronLeft
            className={`h-4 w-4 transition-transform ${collapsed ? "rotate-180" : ""}`}
          />
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-[280px] max-w-[85vw] flex-col bg-sidebar border-r border-sidebar-border lg:hidden transition-transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ top: "48px" }}
      >
        {sidebarContent}
      </aside>

      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-sidebar border-r border-sidebar-border shrink-0 min-h-0 transition-[width] duration-200 ${
          collapsed ? "w-14" : "w-60"
        }`}
      >
        {sidebarContent}
      </aside>

      {/* Mobile hamburger trigger - exposed via data attribute for TopNav */}
      <input
        type="hidden"
        id="sidebar-mobile-toggle"
        data-open={mobileOpen}
        onClick={() => setMobileOpen(!mobileOpen)}
      />
    </>
  );
}

export function useSidebarMobileToggle() {
  return () => {
    const el = document.getElementById("sidebar-mobile-toggle");
    if (el) el.click();
  };
}
